import { io, type Socket } from 'socket.io-client';
import { RealtimeEvent, type RealtimeEventMap } from '@taxi/shared';
import { SimpleObserver } from '@common/libs/observer';

export type RealtimeStatus = 'idle' | 'connecting' | 'connected' | 'polling' | 'offline';

export interface RealtimeConfig {
	url: string;
	path?: string;
	/** Fournit le jeton au moment de la connexion — il peut avoir été rafraîchi. */
	getToken: () => string | null;
	/** Intervalle du repli par interrogation, en millisecondes. */
	pollingIntervalMs?: number;
}

/**
 * Canal temps réel de l'application.
 *
 * Deux modes, transparents pour l'appelant :
 *  - WebSocket quand l'API tourne en serveur long-vivant ;
 *  - interrogation périodique sinon (déploiement Lambda, réseau bloquant les
 *    WebSocket, réseaux d'hôtels filtrants).
 *
 * Le repli est automatique : si la socket ne s'établit pas, on bascule en
 * interrogation et l'application continue de fonctionner, simplement moins vite.
 */
export class Realtime {
	readonly statusObserver = new SimpleObserver<RealtimeStatus>();
	/** Déclenché à chaque cycle d'interrogation, quand la socket n'est pas disponible. */
	readonly pollObserver = new SimpleObserver<void>();

	private socket: Socket | null = null;
	private pollingTimer: ReturnType<typeof setInterval> | null = null;
	private handlers = new Map<string, Set<(payload: any) => void>>();
	private joinedRooms = new Set<string>();
	private _status: RealtimeStatus = 'idle';

	constructor(private readonly config: RealtimeConfig) {}

	get status(): RealtimeStatus {
		return this._status;
	}

	get isConnected(): boolean {
		return this._status === 'connected';
	}

	connect(): void {
		const token = this.config.getToken();
		if (!token) {
			return;
		}
		if (this.socket) {
			this.disconnect();
		}

		this.setStatus('connecting');
		this.socket = io(this.config.url, {
			path: this.config.path ?? '/realtime',
			auth: { token },
			transports: ['websocket', 'polling'],
			reconnection: true,
			reconnectionDelay: 1000,
			reconnectionDelayMax: 10_000,
			timeout: 8000,
		});

		this.socket.on('connect', () => {
			this.setStatus('connected');
			this.stopPolling();
			// Réadhésion aux salons : une reconnexion repart d'une socket vierge.
			for (const room of this.joinedRooms) {
				this.socket?.emit('ride:subscribe', { rideId: Number(room.split(':')[1]) });
			}
		});

		this.socket.on('disconnect', () => {
			this.setStatus('offline');
			this.startPolling();
		});

		this.socket.on('connect_error', () => {
			// WebSocket impossible : on continue en interrogation plutôt que de
			// laisser l'écran figé.
			this.setStatus('polling');
			this.startPolling();
		});

		for (const event of Object.values(RealtimeEvent)) {
			this.socket.on(event, (payload: unknown) => this.dispatch(event, payload));
		}
	}

	disconnect(): void {
		this.socket?.removeAllListeners();
		this.socket?.disconnect();
		this.socket = null;
		this.stopPolling();
		this.setStatus('idle');
	}

	/** Abonnement typé à un événement. Retourne la fonction de désabonnement. */
	on<E extends RealtimeEvent>(
		event: E,
		handler: (payload: RealtimeEventMap[E]) => void,
	): () => void {
		if (!this.handlers.has(event)) {
			this.handlers.set(event, new Set());
		}
		this.handlers.get(event)?.add(handler as (payload: any) => void);
		return () => {
			this.handlers.get(event)?.delete(handler as (payload: any) => void);
		};
	}

	/** Rejoint le suivi d'une course. L'API arbitre l'adhésion, pas le client. */
	subscribeRide(rideId: number): void {
		this.joinedRooms.add(`ride:${rideId}`);
		this.socket?.emit('ride:subscribe', { rideId });
	}

	unsubscribeRide(rideId: number): void {
		this.joinedRooms.delete(`ride:${rideId}`);
		this.socket?.emit('ride:unsubscribe', { rideId });
	}

	subscribeBackoffice(): void {
		this.socket?.emit('backoffice:subscribe');
	}

	private dispatch(event: string, payload: unknown): void {
		for (const handler of this.handlers.get(event) ?? []) {
			try {
				handler(payload);
			} catch (error) {
				console.error(`Gestionnaire temps réel en erreur (${event}) :`, error);
			}
		}
	}

	/**
	 * Repli par interrogation.
	 *
	 * On n'émet pas d'événements ici : on signale simplement qu'il faut recharger
	 * l'état. Les stores s'y abonnent et rafraîchissent ce qui les concerne, sans
	 * dupliquer la logique de rendu entre les deux modes.
	 */
	private startPolling(): void {
		if (this.pollingTimer) {
			return;
		}
		this.pollingTimer = setInterval(() => {
			this.pollObserver.trigger();
		}, this.config.pollingIntervalMs ?? 5000);
	}

	private stopPolling(): void {
		if (this.pollingTimer) {
			clearInterval(this.pollingTimer);
			this.pollingTimer = null;
		}
	}

	private setStatus(status: RealtimeStatus): void {
		this._status = status;
		this.statusObserver.trigger(status);
	}
}
